import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

export function DeleteEmployeeDialog({ employeeId, employeeName, open, onOpenChange, onSuccess, onTerminateInstead }) {
  const handleDelete = async () => {
    try {
      await api.delete(`/employees/${employeeId}`);
      toast.success("Colaborador removido");
      onOpenChange(false);
      onSuccess?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md border-white/10" data-testid="delete-dialog">
        <DialogHeader>
          <DialogTitle>Confirmar exclusão{employeeName ? ` — ${employeeName}` : ""}</DialogTitle>
          <DialogDescription>Esta ação é irreversível e remove o colaborador, seus recebíveis e todo o histórico.</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 space-y-2">
            <p className="text-xs text-yellow-400 font-medium">Considere rescindir em vez de excluir</p>
            <p className="text-[11px] text-muted-foreground">A rescisão mantém o histórico do colaborador (lançamentos, antecipações, fechamentos). A exclusão apaga tudo permanentemente.</p>
            {onTerminateInstead && (
              <Button variant="outline" size="sm" data-testid="suggest-terminate-btn"
                onClick={() => { onOpenChange(false); onTerminateInstead(); }}
                className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 text-xs mt-1">
                Rescindir em vez de excluir
              </Button>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">Cancelar</Button>
          <Button data-testid="confirm-delete-btn" onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700 text-white">Excluir permanentemente</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TerminateEmployeeDialog({ employeeId, employeeName, open, onOpenChange, onSuccess }) {
  const [terminateDate, setTerminateDate] = useState("");
  const [terminating, setTerminating] = useState(false);

  const handleTerminate = async () => {
    if (!terminateDate) { toast.error("Selecione a data de rescisão"); return; }
    setTerminating(true);
    try {
      await api.post(`/employees/${employeeId}/terminate`, { termination_date: terminateDate });
      toast.success(`${employeeName || "Colaborador"} rescindido em ${terminateDate}`);
      onOpenChange(false);
      setTerminateDate("");
      onSuccess?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao rescindir"); }
    setTerminating(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTerminateDate(""); }}>
      <DialogContent className="sm:max-w-md border-white/10" data-testid="terminate-dialog">
        <DialogHeader>
          <DialogTitle>Rescindir Colaborador</DialogTitle>
          <DialogDescription>Após a rescisão, o colaborador não poderá mais solicitar antecipações.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Data de rescisão</Label>
            <Input data-testid="terminate-date-input" type="date" value={terminateDate}
              onChange={(e) => setTerminateDate(e.target.value)}
              className="bg-black/20 border-white/10" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">Cancelar</Button>
          <Button data-testid="confirm-terminate-btn" onClick={handleTerminate} disabled={terminating || !terminateDate}
            className="bg-orange-600 hover:bg-orange-700 text-white font-semibold">
            {terminating ? <div className="loading-spinner" /> : "Confirmar Rescisão"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function UndoTerminateDialog({ employeeId, employeeName, open, onOpenChange, onSuccess }) {
  const [undoing, setUndoing] = useState(false);

  const handleUndo = async () => {
    setUndoing(true);
    try {
      await api.post(`/employees/${employeeId}/undo-terminate`);
      toast.success(`Rescisão de ${employeeName || "colaborador"} desfeita`);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao desfazer rescisão"); }
    setUndoing(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm border-white/10" data-testid="undo-terminate-dialog">
        <DialogHeader>
          <DialogTitle>Desfazer Rescisão</DialogTitle>
          <DialogDescription>O colaborador voltará ao status ativo e seus recebíveis do mês serão regenerados.</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">Cancelar</Button>
          <Button data-testid="confirm-undo-terminate-btn" onClick={handleUndo} disabled={undoing}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
            {undoing ? <div className="loading-spinner" /> : "Confirmar Reativação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
